import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Play, Heart, Dumbbell, ChevronDown, ChevronUp, Pause, Timer, Repeat, RotateCcw, CheckSquare, Square } from 'lucide-react';
import { InlineVideoPlayer, getGDriveThumbnail } from '../VideoPlayer';
import { EQUIPMENT_LABELS, MUSCLE_COLORS } from './constants';
import RestTimer from './RestTimer';
import FeatureHint from '../FeatureHint';
import YumixTip from '../YumixTip';
import { useCoachTip } from '../../constants/coachTips';

export default function ExerciseCard({ ex, language, isFav, onToggleFav, onFullscreen, showValidationHint = false }) {
  const tempoTip = useCoachTip('exercise.tempo');
  const [showVideo, setShowVideo] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sets, setSets] = useState(ex.sets || 3);
  const [reps, setReps] = useState(ex.reps || 12);
  const [tempo, setTempo] = useState(ex.tempo || '2-0-2');
  const [rest, setRest] = useState(ex.rest_seconds || 60);
  const [completedSets, setCompletedSets] = useState([]);
  const [showRestTimer, setShowRestTimer] = useState(false);

  const fr = language === 'fr';
  const gradient = MUSCLE_COLORS[ex.muscle_groups?.[0]] || 'from-primary/20 to-primary/5';
  const hasVideo = !!(ex.video_url || ex.external_video_url);
  // Auto-derive Drive thumbnail so the card shows a real frame instead of a black square
  const thumbnail = hasVideo ? (ex.thumbnail_url || getGDriveThumbnail(ex.video_url || ex.external_video_url)) : null;

  const toggleSet = (setIndex) => {
    const isCompleting = !completedSets.includes(setIndex);
    setCompletedSets(prev =>
      prev.includes(setIndex) ? prev.filter(s => s !== setIndex) : [...prev, setIndex]
    );
    // Auto-start rest timer when completing a set (not the last one)
    if (isCompleting && setIndex < sets - 1) {
      setShowRestTimer(true);
    }
  };

  return (
    <Card className="bg-card border-border overflow-hidden group" data-testid={`exercise-${ex.exercise_id}`}>
      <div className="relative">
        {showVideo && hasVideo ? (
          <InlineVideoPlayer
            videoUrl={ex.video_url}
            externalVideoUrl={ex.external_video_url}
            thumbnailUrl={thumbnail}
            name={ex.name_fr}
            onClose={() => setShowVideo(false)}
            onFullscreen={onFullscreen}
            autoPlay
          />
        ) : (
          <div
            className={`flex items-center justify-center relative cursor-pointer bg-gradient-to-br ${gradient} overflow-hidden`}
            style={{ aspectRatio: '16 / 9' }}
            onClick={() => hasVideo && setShowVideo(true)}
            data-testid="video-thumbnail"
          >
            {hasVideo && thumbnail && (
              <img
                src={thumbnail}
                alt={ex.name_fr}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            {hasVideo && thumbnail && <div className="absolute inset-0 bg-black/30" />}

            {hasVideo ? (
              <div className="relative flex flex-col items-center gap-1.5">
                <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/25 transition-colors">
                  <Play className="w-6 h-6 text-white ml-0.5" />
                </div>
                <span className="text-[9px] uppercase tracking-widest text-white/70 font-semibold">
                  {ex.muscle_groups?.[0] || ''}
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1.5">
                <Dumbbell className="w-8 h-8 text-foreground/20" />
                <span className="text-[9px] uppercase tracking-widest text-foreground/15 font-semibold">
                  {ex.muscle_groups?.[0] || ''}
                </span>
              </div>
            )}

            <button
              onClick={e => { e.stopPropagation(); onToggleFav(ex.exercise_id); }}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors z-10"
              data-testid={`fav-btn-${ex.exercise_id}`}
            >
              <Heart className={`w-4 h-4 transition-colors ${isFav ? 'text-red-400 fill-red-400' : 'text-white/60'}`} />
            </button>

            {hasVideo && (
              <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-black/50 text-white text-[9px] rounded flex items-center gap-1">
                <Play className="w-2.5 h-2.5" /> VIDEO
              </div>
            )}
          </div>
        )}
      </div>

      <CardContent className="p-3">
        <h3 className="font-heading text-sm uppercase tracking-tight text-foreground leading-snug flex-1">
          {language === 'fr' ? ex.name_fr : (ex.name_en || ex.name_fr)}
        </h3>

        <div className="flex flex-wrap gap-1 mt-1.5">
          {ex.muscle_groups?.map(mg => (
            <span key={mg} className="text-[9px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-medium">{mg}</span>
          ))}
          <span className="text-[9px] px-1.5 py-0.5 bg-secondary text-muted-foreground rounded">
            {EQUIPMENT_LABELS[ex.equipment]?.[language] || ex.equipment}
          </span>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full mt-2 py-1.5 flex items-center justify-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors rounded border border-border/50 hover:border-primary/30"
          data-testid="expand-params-btn"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {language === 'fr' ? 'Paramètres' : 'Parameters'}
        </button>

        {expanded && (
          <div className="mt-2 grid grid-cols-2 gap-2 animate-in slide-in-from-top-2 duration-200" data-testid="workout-params">
            <div className="space-y-1">
              <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                <Repeat className="w-3 h-3" />
                {language === 'fr' ? 'Séries' : 'Sets'}
              </label>
              <div className="flex items-center gap-1">
                <button onClick={() => setSets(Math.max(1, sets - 1))} className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-xs hover:bg-primary/20">-</button>
                <span className="text-sm font-bold text-foreground w-6 text-center">{sets}</span>
                <button onClick={() => setSets(Math.min(10, sets + 1))} className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-xs hover:bg-primary/20">+</button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                <RotateCcw className="w-3 h-3" />
                {language === 'fr' ? 'Répétitions' : 'Reps'}
              </label>
              <div className="flex items-center gap-1">
                <button onClick={() => setReps(Math.max(1, reps - 1))} className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-xs hover:bg-primary/20">-</button>
                <span className="text-sm font-bold text-foreground w-6 text-center">{reps}</span>
                <button onClick={() => setReps(Math.min(50, reps + 1))} className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-xs hover:bg-primary/20">+</button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                <Pause className="w-3 h-3" />
                Tempo
                {tempoTip && (
                  <YumixTip
                    id={tempoTip.id}
                    language={tempoTip.language}
                    size="sm"
                    side="top"
                    title={tempoTip.title}
                    text={tempoTip.text}
                  />
                )}
              </label>
              <select value={tempo} onChange={e => setTempo(e.target.value)} className="w-full h-7 text-xs bg-secondary rounded border-0 text-foreground px-1.5" data-testid="tempo-select">
                <option value="2-0-2">2-0-2</option>
                <option value="3-1-1">3-1-1</option>
                <option value="4-0-1">4-0-1</option>
                <option value="3-0-3">3-0-3</option>
                <option value="2-1-2">2-1-2</option>
                <option value="5-0-1">5-0-1</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                <Timer className="w-3 h-3" />
                {language === 'fr' ? 'Repos' : 'Rest'}
              </label>
              <select value={rest} onChange={e => setRest(Number(e.target.value))} className="w-full h-7 text-xs bg-secondary rounded border-0 text-foreground px-1.5" data-testid="rest-select">
                <option value={30}>30s</option>
                <option value={45}>45s</option>
                <option value={60}>60s</option>
                <option value={90}>90s</option>
                <option value={120}>2 min</option>
                <option value={180}>3 min</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Set Tracking — checkboxes to mark completed sets */}
        <div className="mt-2.5 space-y-1" data-testid="set-tracker">
          <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
            {fr ? 'Series' : 'Sets'} ({completedSets.length}/{sets})
          </p>
          <div className="flex gap-1.5 flex-wrap">
            {Array.from({ length: sets }, (_, i) => {
              const done = completedSets.includes(i);
              const setBtn = (
                <button
                  key={i}
                  onClick={() => toggleSet(i)}
                  data-testid={`set-check-${i}`}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${
                    done
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {done ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                  Set {i + 1}
                </button>
              );
              if (showValidationHint && i === 0) {
                return (
                  <FeatureHint
                    key={i}
                    id="workout-validate-set"
                    title={fr ? 'Valider tes séries' : 'Log your sets'}
                    body={fr
                      ? 'Clique ici pour valider chaque série ➡️. Le timer de récupération ⏱️ démarre automatiquement et la prochaine série est prête.'
                      : 'Click here to log each set ➡️. The rest timer ⏱️ starts automatically and the next set is ready.'}
                    side="top"
                    align="start"
                    dismissOnInteract={true}
                  >
                    {setBtn}
                  </FeatureHint>
                );
              }
              return setBtn;
            })}
          </div>
        </div>
        
        {/* Rest Timer Overlay */}
        {showRestTimer && (
          <RestTimer
            restSeconds={rest}
            language={language}
            onComplete={() => {}}
            onDismiss={() => setShowRestTimer(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}
